import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ErrorCode } from '@brimo/shared';
import { ApiError, api, errorMessage } from '../../lib/api';
import { Banner, Button, Card, Skeleton, TextField, TextLink } from '../../ds/components';
import { AuthFrame } from './AuthFrame';
import { PASSWORD_MISMATCH, PASSWORD_POLICY_HINT, useForgotPassword } from './auth';
import styles from './LoginPage.module.css';

/** `/forgot-password`: requests the 60-minute reset link; the answer never reveals whether the address exists. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const forgot = useForgotPassword();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    forgot.mutate(email.trim());
  }

  return (
    <AuthFrame>
      <Card padded>
        <h1 className={styles.title}>Passwort zurücksetzen</h1>
        <p className={styles.subtitle}>
          Wir senden Ihnen einen Link an Ihre geschäftliche E-Mail-Adresse. Der Link ist 60 Minuten gültig.
        </p>
        {forgot.isSuccess ? (
          <>
            <div className={styles.banner}>
              <Banner tone="success">
                Wenn ein Konto zu {email.trim()} existiert, ist der Link unterwegs. Prüfen Sie auch den
                Spam-Ordner.
              </Banner>
            </div>
            <div className={styles.form}>
              <Button variant="secondary" block onClick={() => forgot.reset()}>
                Erneut senden
              </Button>
              <div className={styles.links}>
                <TextLink to="/login" tone="muted">
                  Zurück zur Anmeldung
                </TextLink>
              </div>
            </div>
          </>
        ) : (
          <>
            {forgot.isError ? (
              <div className={styles.banner}>
                <Banner tone="danger">{errorMessage(forgot.error)}</Banner>
              </div>
            ) : null}
            <form className={styles.form} onSubmit={submit} noValidate>
              <TextField
                label="E-Mail"
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <Button type="submit" block loading={forgot.isPending}>
                Link senden
              </Button>
              <div className={styles.links}>
                <TextLink to="/login" tone="muted">
                  Zurück zur Anmeldung
                </TextLink>
              </div>
            </form>
          </>
        )}
      </Card>
    </AuthFrame>
  );
}

/** `/reset/:token`: sets the new password (policy §9.1); lifts the lock and ends every session (P-13). */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const params = useParams();
  const token = params['token'] ?? '';
  const lookup = useQuery({
    queryKey: ['reset', token],
    queryFn: () => api<{ email: string }>(`/auth/reset/${encodeURIComponent(token)}`),
    retry: false,
    enabled: token.length > 0,
  });
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const reset = useMutation({
    mutationFn: () => api<void>('/auth/reset-password', { method: 'POST', body: { token, password } }),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    reset.mutate();
  }

  const expired =
    (lookup.isError && lookup.error instanceof ApiError && lookup.error.code === ErrorCode.LINK_EXPIRED) ||
    (reset.isError && reset.error instanceof ApiError && reset.error.code === ErrorCode.LINK_EXPIRED);
  const unknown = lookup.isError && !expired;
  const fieldError =
    reset.isError && reset.error instanceof ApiError ? reset.error.fields['password'] : undefined;

  if (reset.isSuccess) {
    return (
      <AuthFrame>
        <Card padded>
          <h1 className={styles.title}>Passwort geändert.</h1>
          <p className={styles.subtitle}>
            Ihr Konto ist wieder entsperrt. Melden Sie sich mit dem neuen Passwort an — alle anderen Sitzungen
            wurden beendet.
          </p>
          <div className={styles.form}>
            <Button
              block
              onClick={() =>
                navigate('/login', {
                  replace: true,
                  state: {
                    message: 'Passwort geändert. Melden Sie sich mit dem neuen Passwort an.',
                    tone: 'success',
                  },
                })
              }
            >
              Zur Anmeldung
            </Button>
          </div>
        </Card>
      </AuthFrame>
    );
  }

  if (expired || unknown || token.length === 0) {
    return (
      <AuthFrame>
        <Card padded>
          <h1 className={styles.title}>
            {expired ? 'Dieser Link ist abgelaufen.' : 'Dieser Link ist ungültig.'}
          </h1>
          <p className={styles.subtitle}>
            {expired
              ? 'Ein Link gilt 60 Minuten und nur einmal. Fordern Sie einen neuen Link an.'
              : 'Der Link wurde nicht gefunden. Fordern Sie einen neuen Link an.'}
          </p>
          <div className={styles.form}>
            <Button block onClick={() => navigate('/forgot-password')}>
              Neuen Link anfordern
            </Button>
            <div className={styles.links}>
              <TextLink to="/login" tone="muted">
                Zurück zur Anmeldung
              </TextLink>
            </div>
          </div>
        </Card>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <Card padded>
        <h1 className={styles.title}>Neues Passwort festlegen</h1>
        <p className={styles.subtitle}>
          {lookup.data ? (
            <>
              Für das Konto {lookup.data.email}. Der Link entsperrt das Konto, sobald das neue Passwort
              gesetzt ist.
            </>
          ) : (
            <Skeleton width="70%" />
          )}
        </p>
        {reset.isError && !fieldError ? (
          <div className={styles.banner}>
            <Banner tone="danger">{errorMessage(reset.error)}</Banner>
          </div>
        ) : null}
        <form className={styles.form} onSubmit={submit} noValidate>
          <TextField
            label="Neues Passwort"
            type="password"
            name="new-password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint={PASSWORD_POLICY_HINT}
            error={fieldError}
          />
          <TextField
            label="Passwort wiederholen"
            type="password"
            name="repeat-password"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            error={mismatch ? PASSWORD_MISMATCH : undefined}
          />
          <Button type="submit" block loading={reset.isPending} disabled={!lookup.data}>
            Passwort speichern
          </Button>
          <div className={styles.links}>
            <TextLink to="/login" tone="muted">
              Zurück zur Anmeldung
            </TextLink>
          </div>
        </form>
      </Card>
    </AuthFrame>
  );
}
